// Factory function to create DesignSystemContent from ds_files array
import { DsFile, DesignSystemFont, IconPackId } from 'dragonmouse-shared/interfaces';
import { bundleDsFiles, parseDsDisplay } from 'dragonmouse-shared/ds';
import { DEFAULT_ICON_PACK, DEFAULT_DS_FONT } from 'dragonmouse-shared/constants';
import {
	DesignSystemContent,
	DesignSystemGuidelineItem,
	DesignSystemConfig,
	DesignSystemAsset,
	AssetCategory,
} from './types';
import { DesignSystemGalleryItem } from '../types';
import { DesignSystemToken, ComponentCategory, DesignSystemRecipe, TokenCategory } from '../types';
import { parseTokens } from 'designSystem/tokens/parser';
import { parseRecipes } from 'designSystem/recipes/parser';
import { parseComponents } from 'designSystem/components/parser';
import { parseGalleryFile } from '../gallery/parser';
import { displayNameFromPath } from './util';
import { buildDependencyGraph } from './graph';

export function createDesignSystemContentFromFiles(
	versionId: string,
	files: DsFile[]
): DesignSystemContent {
	// Parse config/display.json once — used for both bundler and config
	const dsDisplay = parseDsDisplay(files);

	// Use shared bundling logic to get all CSS/JS content
	const bundle = bundleDsFiles(files, dsDisplay?.modes);

	// Find theme files and styles/tokens*.css for token parsing
	const themeFiles = files.filter((f) => f.path.startsWith('theme/') && f.path.endsWith('.css'));
	const stylesTokensFiles = files.filter(
		(f) => f.path.startsWith('styles/tokens') && f.path.endsWith('.css')
	);

	// Find recipe files for parsing
	const recipeFiles = files.filter(
		(f) => f.path.startsWith('styles/recipes/') && f.path.endsWith('.css')
	);

	// Find all component CSS files and create a map
	const componentCssFiles = files.filter(
		(f) => f.path.startsWith('components/') && f.path.endsWith('.css')
	);
	const cssMap = new Map<string, string>();
	for (const cssFile of componentCssFiles) {
		cssMap.set(cssFile.path, cssFile.content);
	}

	// Find all component example files and create a map
	const componentExampleFiles = files.filter(
		(f) => f.path.startsWith('components/') && f.path.endsWith('.example.json')
	);
	const exampleMap = new Map<string, { props?: Record<string, any>; children?: string }>();
	for (const exampleFile of componentExampleFiles) {
		// Derive tag name from path: components/button/button.example.json -> ds-button
		const pathParts = exampleFile.path.split('/');
		const filename = pathParts[pathParts.length - 1];
		const componentName = filename.replace('.example.json', '');
		const tagName = `ds-${componentName}`;

		try {
			const exampleData = JSON.parse(exampleFile.content) as {
				props?: Record<string, any>;
				children?: string;
			};
			exampleMap.set(tagName, exampleData);
		} catch (error) {
			console.warn(`Failed to parse ${exampleFile.path}:`, error);
		}
	}

	// Find all component guidelines files and create a map
	const componentGuidelinesFiles = files.filter(
		(f) => f.path.startsWith('components/') && f.path.endsWith('.guidelines.md')
	);
	const guidelinesMap = new Map<string, string>();
	for (const guidelinesFile of componentGuidelinesFiles) {
		const pathParts = guidelinesFile.path.split('/');
		const filename = pathParts[pathParts.length - 1];
		const componentName = filename.replace('.guidelines.md', '');
		const tagName = `ds-${componentName}`;
		guidelinesMap.set(tagName, guidelinesFile.content);
	}

	// Find all gallery HTML files
	const galleryFiles = files.filter(
		(f) => f.path.startsWith('gallery/') && f.path.endsWith('.html')
	);

	// Find all guidelines files — exclude *.summary.md (LLM-only context, not user-facing)
	const guidelinesFiles = files.filter(
		(f) => f.path.startsWith('guidelines/') && !f.path.endsWith('.summary.md')
	);

	// Find all asset files
	const assetFiles = files.filter((f) => f.path.startsWith('assets/'));

	// Parse theme tokens separately from styles tokens
	const modeIds = dsDisplay?.modes?.map((m) => m.id) ?? [];
	let themeTokens: ReturnType<typeof parseTokens> = [];
	let stylesTokens: ReturnType<typeof parseTokens> = [];

	// Parse all theme CSS files (pass filename for category inference, modeIds for mode stamping)
	for (const themeFile of themeFiles) {
		themeTokens = [...themeTokens, ...parseTokens(themeFile.content, themeFile.path, modeIds)];
	}

	// Parse all styles/tokens*.css files (base + dark mode overrides)
	for (const stylesTokensFile of stylesTokensFiles) {
		stylesTokens = [
			...stylesTokens,
			...parseTokens(stylesTokensFile.content, stylesTokensFile.path, modeIds),
		];
	}

	// Group theme tokens by category
	let parsedTheme: DesignSystemContent['theme'] | undefined;
	if (themeTokens.length > 0) {
		const byCategory: Record<TokenCategory, typeof themeTokens> = {
			color: [],
			typography: [],
			spacing: [],
			elevation: [],
			borderRadius: [],
			opacity: [],
			motion: [],
			other: [],
		};

		const byId: Record<string, (typeof themeTokens)[0]> = {};

		for (const token of themeTokens) {
			byCategory[token.category].push(token);
			byId[token.id] = token;
		}

		parsedTheme = {
			byCategory,
			byId,
			all: themeTokens,
		};
	}

	// Parse all recipe files
	let allRecipes: DesignSystemRecipe[] = [];
	for (const recipeFile of recipeFiles) {
		allRecipes = [...allRecipes, ...parseRecipes(recipeFile.content, recipeFile.path)];
	}

	// Group styles tokens by category and build styles object
	let parsedStylesTokens: DesignSystemContent['styles'] | undefined;
	if (stylesTokens.length > 0 || allRecipes.length > 0) {
		parsedStylesTokens = {};

		if (stylesTokens.length > 0) {
			const byCategory: Record<TokenCategory, typeof stylesTokens> = {
				color: [],
				typography: [],
				spacing: [],
				elevation: [],
				borderRadius: [],
				opacity: [],
				motion: [],
				other: [],
			};

			const byId: Record<string, (typeof stylesTokens)[0]> = {};

			for (const token of stylesTokens) {
				byCategory[token.category].push(token);
				byId[token.id] = token;
			}

			parsedStylesTokens.tokens = {
				byCategory,
				byId,
				all: stylesTokens,
			};
		}

		if (allRecipes.length > 0) {
			const byDomain: Record<string, DesignSystemRecipe[]> = {};
			const byId: Record<string, DesignSystemRecipe> = {};

			for (const recipe of allRecipes) {
				if (!byDomain[recipe.domain]) {
					byDomain[recipe.domain] = [];
				}
				byDomain[recipe.domain].push(recipe);
				byId[recipe.id] = recipe;
			}

			parsedStylesTokens.recipes = {
				byDomain,
				byId,
				all: allRecipes,
			};
		}
	}

	// Use the IIFE-wrapped bundle for parsing (avoids const/let collisions across components)
	const combinedComponentsContent = bundle.componentsJs;

	// Parse all component JS files
	let parsedComponents: DesignSystemContent['components'] | undefined;
	if (combinedComponentsContent.length > 0) {
		const components = parseComponents(
			combinedComponentsContent,
			cssMap,
			exampleMap,
			guidelinesMap
		);

		// Group components by category
		const byCategory: Record<ComponentCategory, typeof components> = {
			button: [],
			card: [],
			form: [],
			navigation: [],
			'data-display': [],
			other: [],
		};

		const byId: Record<string, (typeof components)[0]> = {};

		for (const component of components) {
			byCategory[component.category].push(component);
			byId[component.id] = component;
		}

		parsedComponents = {
			byCategory,
			byId,
			all: components,
		};
	}

	// Parse each gallery file individually (one entry per file)
	const parsedGallery: DesignSystemGalleryItem[] = galleryFiles
		.map((f) => parseGalleryFile(f.path, f.content))
		.filter((item): item is DesignSystemGalleryItem => item !== null);

	// Combine all gallery files content for rawCode (download/display)
	const combinedGalleryContent =
		galleryFiles.length > 0 ? galleryFiles.map((f) => f.content).join('\n\n') : '';

	// Parse guidelines files into individual items
	const parsedGuidelines: DesignSystemGuidelineItem[] = guidelinesFiles.map((file) => ({
		title: displayNameFromPath(file.path, 'guidelines'),
		path: file.path,
		content: file.content,
	}));

	// Combine all guidelines files content for backward compatibility in rawCode
	const combinedGuidelinesContent =
		guidelinesFiles.length > 0 ? guidelinesFiles.map((f) => f.content).join('\n\n') : '';

	// Parse config files (config/icons.json, config/fonts.json, config/display.json, config/meta.json)
	let config: DesignSystemConfig | undefined;
	const iconsFile = files.find((f) => f.path === 'config/icons.json');
	const fontsFile = files.find((f) => f.path === 'config/fonts.json');
	const metaFile = files.find((f) => f.path === 'config/meta.json');

	if (iconsFile || fontsFile || dsDisplay || metaFile) {
		config = {
			iconPack: DEFAULT_ICON_PACK,
			fonts: [DEFAULT_DS_FONT],
		};

		if (iconsFile) {
			try {
				const iconsConfig = JSON.parse(iconsFile.content) as { pack: IconPackId };
				if (iconsConfig.pack) {
					config.iconPack = iconsConfig.pack;
				}
			} catch (error) {
				console.warn('Failed to parse config/icons.json:', error);
			}
		}

		if (fontsFile) {
			try {
				const parsedFonts = JSON.parse(fontsFile.content) as DesignSystemFont[];
				if (parsedFonts.length > 0) {
					config.fonts = parsedFonts;
				}
			} catch (error) {
				console.warn('Failed to parse config/fonts.json:', error);
			}
		}

		if (metaFile) {
			try {
				const parsed = JSON.parse(metaFile.content);
				config.meta = { description: typeof parsed.description === 'string' ? parsed.description : '' };
			} catch {
				// ignore malformed meta.json
			}
		}

		if (dsDisplay) {
			config.display = dsDisplay;
		}
	}

	// Parse asset files
	let parsedAssets: DesignSystemContent['assets'] | undefined;
	if (assetFiles.length > 0) {
		// Helper function to get MIME type from extension
		const getMimeType = (ext: string): string => {
			const mimeTypes: Record<string, string> = {
				svg: 'image/svg+xml',
				png: 'image/png',
				jpg: 'image/jpeg',
				jpeg: 'image/jpeg',
				gif: 'image/gif',
				webp: 'image/webp',
				ico: 'image/x-icon',
			};
			return mimeTypes[ext.toLowerCase()] || 'application/octet-stream';
		};

		const validCategories: AssetCategory[] = [
			'logos',
			'icons',
			'illustrations',
			'patterns',
			'other',
		];
		const parsedAssetList: DesignSystemAsset[] = assetFiles.map((file) => {
			const pathParts = file.path.split('/');
			const rawCategory = pathParts[1];
			const category: AssetCategory = validCategories.includes(rawCategory as AssetCategory)
				? (rawCategory as AssetCategory)
				: 'other';
			const filename = pathParts[pathParts.length - 1];
			const ext = filename.split('.').pop() || '';
			const mimeType = getMimeType(ext);

			return {
				path: file.path,
				casHash: file.content,
				category,
				name: displayNameFromPath(file.path, 'assets'),
				mimeType,
			};
		});

		// Group assets by category
		const byCategory: Record<AssetCategory, DesignSystemAsset[]> = {
			logos: [],
			icons: [],
			illustrations: [],
			patterns: [],
			other: [],
		};

		for (const asset of parsedAssetList) {
			byCategory[asset.category].push(asset);
		}

		parsedAssets = {
			byCategory,
			all: parsedAssetList,
		};
	}

	const graph = buildDependencyGraph(files);
	const missingFonts = findMissingFonts(config, [
		...(parsedTheme?.all ?? []),
		...(parsedStylesTokens?.tokens?.all ?? []),
	]);

	return {
		versionId,
		graph,
		missingFonts: missingFonts.length > 0 ? missingFonts : undefined,
		files,
		theme: parsedTheme,
		styles: parsedStylesTokens,
		components: parsedComponents,
		rawCode: {
			theme: bundle.theme,
			styles: bundle.styles,
			componentsCss: bundle.componentsCss,
			componentsJs: bundle.componentsJs,
			gallery: combinedGalleryContent || undefined,
			guidelines: combinedGuidelinesContent || undefined,
		},
		gallery: parsedGallery.length > 0 ? parsedGallery : undefined,
		guidelines: parsedGuidelines.length > 0 ? parsedGuidelines : undefined,
		config,
		assets: parsedAssets,
	};
}

const CSS_GENERIC_FAMILIES = new Set([
	'sans-serif', 'serif', 'monospace', 'system-ui', 'cursive', 'fantasy',
]);

// Checks if the primary (first non-generic) font in each font stack is in fonts.json
function findMissingFonts(
	config: DesignSystemConfig | undefined,
	tokens: DesignSystemToken[]
): string[] {
	if (!config?.fonts || config.fonts.length === 0) return [];

	const configLower = new Set(config.fonts.map((f) => f.fontFamily.toLowerCase()));
	const missing: string[] = [];

	for (const token of tokens) {
		if (token.value.kind !== 'fontFamily') continue;
		const primary = token.value.families.find(
			(f) => !CSS_GENERIC_FAMILIES.has(f.trim().toLowerCase())
		);
		if (!primary) continue;
		const name = primary.trim();
		if (!configLower.has(name.toLowerCase()) && !missing.includes(name)) {
			missing.push(name);
		}
	}

	return missing;
}
